import axios from "axios";
import { isRegistrationPublicApiUrl, normalizeRequestHeaders } from "./api-request-headers";

const normalizeApiBase = (url: string): string => {
  const trimmed = url.trim().replace(/\/+$/, "");
  // Same-origin proxy path (production): /api/v1 → cookies stay on www / tenant host
  if (trimmed.startsWith("/")) {
    return `${trimmed}/`;
  }
  return `${trimmed}/`;
};

const getApiUrl = (): string => {
  if (typeof window !== "undefined" && (window as any).__ENV__?.VITE_API_URL) {
    return normalizeApiBase((window as any).__ENV__.VITE_API_URL);
  }
  const buildTimeUrl = import.meta.env.VITE_API_URL || "/api/v1";
  return normalizeApiBase(buildTimeUrl);
};

const API_URL = getApiUrl();

if (import.meta.env.DEV) {
  console.log("API Base URL:", API_URL);
}

if (import.meta.env.PROD && API_URL.includes("localhost") && !API_URL.startsWith("/")) {
  console.warn("API URL appears to point to localhost in production");
}

export const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
});

// ============================================================================
// Request Interceptor
// Attach tenant slug header from sessionStorage (set after login).
// Access token is sent automatically as an httpOnly cookie.
// ============================================================================

api.interceptors.request.use((config) => {
  normalizeRequestHeaders(config);
  const headers = config.headers as Record<string, string | undefined>;
  const existingSlug = headers["x-tenant-slug"] ?? headers["X-Tenant-Slug"];
  if (!existingSlug) {
    const cachedSlug = sessionStorage.getItem("tenant_slug");
    if (cachedSlug) {
      config.headers["x-tenant-slug"] = cachedSlug;
    }
  }
  return config;
});

// ============================================================================
// Response Interceptor — Handle 401s with one silent-refresh retry
// ============================================================================

let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
  config: any;
}> = [];

const processQueue = (error: any) => {
  failedQueue.forEach((item) => {
    if (error) {
      item.reject(error);
    } else {
      item.resolve(api(item.config));
    }
  });
  failedQueue = [];
};

const performTokenRefresh = async (): Promise<boolean> => {
  try {
    await axios.post(`${API_URL}auth/refresh`, {}, { withCredentials: true });
    return true;
  } catch {
    return false;
  }
};

const refreshWithDedup = async (): Promise<boolean> => {
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }
  isRefreshing = true;
  refreshPromise = performTokenRefresh().finally(() => {
    isRefreshing = false;
    refreshPromise = null;
  });
  return refreshPromise;
};

const PUBLIC_PAGES = [
  "/login",
  "/register",
  "/onboard",
  "/verify-email",
  "/reset-password",
  "/staff/accept-invite",
  "/view/",
  "/opac",
  "/designsystem",
  "/contact",
];

const isOnPublicPage = (): boolean => {
  const pathname = window.location.pathname;
  // Exact "/" — main platform HomePage and tenant TenantLandingPage (do not use includes("/"))
  if (pathname === "/") return true;
  return PUBLIC_PAGES.some((p) => pathname.includes(p)) || pathname.startsWith("/payment/callback");
};

const REDIRECT_COOLDOWN_MS = 5000;
const POST_LOGIN_GRACE_MS = 10000;

// In-memory grace period (not sessionStorage) — survives Safari ITP / private mode restrictions
let _lastLoginTs = 0;

/** Call after successful login to avoid redirecting on transient 401s (mobile cookie timing) */
export function markRecentLogin(): void {
  _lastLoginTs = Date.now();
  try {
    sessionStorage.setItem("_auth_login_ts", String(_lastLoginTs));
  } catch {
    // sessionStorage may fail in private mode — in-memory is primary
  }
}

const safeRedirectToLogin = () => {
  if (isOnPublicPage()) return;
  const lastRedirect = Number(sessionStorage.getItem("_auth_redirect_ts") || "0");
  if (Date.now() - lastRedirect < REDIRECT_COOLDOWN_MS) return;
  // Skip redirect if we just logged in (mobile: cookies may not be available yet)
  const loginTs = _lastLoginTs || Number(sessionStorage.getItem("_auth_login_ts") || "0");
  if (loginTs && Date.now() - loginTs < POST_LOGIN_GRACE_MS) return;
  sessionStorage.setItem("_auth_redirect_ts", String(Date.now()));
  const returnUrl = window.location.pathname + window.location.search;
  window.location.href = `/login?returnUrl=${encodeURIComponent(returnUrl)}`;
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 403 && error.response?.data?.code === "PAYMENT_REQUIRED") {
      const pathname = window.location.pathname;
      const isOnPaymentSafePage =
        pathname === "/" ||
        pathname.startsWith("/invoices") ||
        pathname.startsWith("/billing") ||
        pathname.startsWith("/login") ||
        pathname.startsWith("/register") ||
        pathname.startsWith("/opac") ||
        pathname.startsWith("/payment/callback");
      if (!isOnPaymentSafePage) {
        window.location.href = "/invoices";
      }
      return Promise.reject(error);
    }

    if (error.response?.status === 403 && error.response?.data?.code === "EMAIL_NOT_VERIFIED") {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      const isAuthEndpoint =
        originalRequest.url?.includes("/auth/login") ||
        originalRequest.url?.includes("/auth/refresh") ||
        originalRequest.url?.includes("/auth/register");

      const isPublicEndpoint =
        originalRequest.url?.includes("/public/") ||
        originalRequest.url?.includes("/guest/opac") ||
        originalRequest.url?.includes("/catalog/public/") ||
        originalRequest.url?.includes("/staff/validate-invite") ||
        originalRequest.url?.includes("/staff/accept-invite") ||
        originalRequest.url?.includes("/payment-callback/") ||
        originalRequest.url?.includes("/contact") ||
        isRegistrationPublicApiUrl(originalRequest.url);

      if (isPublicEndpoint) {
        return Promise.reject(error);
      }

      if (isAuthEndpoint) {
        safeRedirectToLogin();
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject, config: originalRequest });
        });
      }

      const refreshed = await refreshWithDedup();

      if (refreshed) {
        processQueue(null);
        return api(originalRequest);
      } else {
        processQueue(error);
        safeRedirectToLogin();
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  }
);

export default api;
